import React, { useState, useEffect, useRef } from 'react';
import Modal from 'cccisd-modal';
import { Formik, Form, Field, CccisdToggle, CccisdFieldWrapper } from 'cccisd-formik';
import DatePicker from 'cccisd-datepicker';
import { client } from 'cccisd-apollo';
import _get from 'lodash/get';
import axios from 'cccisd-axios';
import notification from 'cccisd-notification';

import RequiredFieldLabel from '../../components/Manage/RequiredFieldLabel';
import ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES from './ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES.json';

import getAllYouthListQuery from './queries/getAllYouthList.graphql';
import getAllPlansListQuery from './queries/getAllPlansList.graphql';
import getAdministeredPlansQuery from './queries/getAdministeredPlans.graphql';

import { isDateInPast, isMonday, getFormattedDateForDB, getPlanStatus } from './helpers.js';
import YouthCard from './components/YouthCard.js';
import PlanCard from './components/PlanCard.js';
import YouthSelectionTable from './components/YouthSelectionTable.js';

import style from './style.css';

const Boilerplate = window.cccisd && window.cccisd.boilerplate;

const Administer = () => {
    const [youthList, setYouthList] = useState([]);
    const [plansList, setPlansList] = useState({});
    const [startDateValue, setStartDateValue] = useState(null);
    const [selectedPlans, setSelectedPlans] = useState({});
    const [selectedYouth, setSelectedYouth] = useState([]);
    const [youthPlans, setYouthPlans] = useState([]);
    const [youthSelectionTableSelectedRows, setYouthSelectionTableSelectedRows] = useState([]);
    const [filters, setFilters] = useState({
        show: 'all',
        sortBy: 'alerts',
        planName: '',
        search: '',
    });
    const [alertsEditedSignal, setAlertsEditedSignal] = useState(true);

    const assignPlanModalRef = useRef();
    const showFilterRef = useRef();
    const sortByFilterRef = useRef();
    const planNameFilterRef = useRef();

    function customYouthSorting(a, b) {
        const sortByValue = (filters.sortBy || '').toLowerCase();

        const firstNameA = a.firstName ?? '';
        const firstNameB = b.firstName ?? '';

        const lastNameNameA = a.lastName ?? '';
        const lastNameNameB = b.lastName ?? '';

        const wrapIDA = a.wrapId ?? '';
        const wrapIDB = b.wrapId ?? '';

        switch (sortByValue) {
            case 'alerts':
                return b.flags.length - a.flags.length;
            case 'first_name':
                return firstNameA.localeCompare(firstNameB);
            case 'last_name':
                return lastNameNameA.localeCompare(lastNameNameB);
            case 'wrapid':
                return wrapIDA.localeCompare(wrapIDB);
            default:
                return 0;
        }
    }

    useEffect(() => {
        // query list of all youth belonging to this Care Coordinator
        async function getAllYouthList() {
            const response = await client.query({
                query: getAllYouthListQuery,
                fetchPolicy: 'network-only',
                variables: {
                    careCoordinatorPawnId: _get(window, 'cccisd.fortress.user.acting.id', '-1'),
                }, // only query for youth that are assigned to this care coordinator
            });

            const youths = _get(response, 'data.roles.youthList', []).map(youth => {
                let fields = youth.fields;

                fields.pawnId = youth.pawn.pawnId;
                fields.caregiverList = youth.parentRoles.caregiverList;

                fields.flags = [];
                youth.flags.forEach(flag => {
                    flag.flagProgressList.forEach(progress => {
                        fields.flags.push({
                            key: youth.pawn.pawnId + '_' + progress.flagId,
                            respondent_pawn_id: youth.pawn.pawnId,
                            critical: progress.flag.name.toLowerCase().includes('critical'),
                            surveyItem: progress.flag.description,
                            response: progress.flag.equation,
                            date: progress.flaggedDate,
                            respondent: 'youth',
                            actions: ['resolve', 'addNote', 'viewLogs'],
                            log: progress.log,
                            notes: progress.notes,
                            flagId: progress.flagId,
                            status: progress.status,
                            unflaggedReason: progress.unflaggedReason,
                            flagProgressId: progress.flagProgressId,
                        });
                    });
                });

                // add the caregiver flags to the flags object
                youth.parentRoles.caregiverList.forEach(caregiver => {
                    caregiver.flags.forEach(flag => {
                        flag.flagProgressList.forEach(progress => {
                            fields.flags.push({
                                key: caregiver.pawn.pawnId + '_' + progress.flagId,
                                respondent_pawn_id: caregiver.pawn.pawnId,
                                critical: progress.flag.name.toLowerCase().includes('critical'),
                                surveyItem: progress.flag.description,
                                response: progress.flag.equation,
                                date: progress.flaggedDate,
                                respondent: 'caregiver',
                                caregiverName: `${caregiver.fields.firstName} ${caregiver.fields.lastName}`,
                                actions: ['resolve', 'addNote', 'viewLogs'],
                                log: progress.log,
                                notes: progress.notes,
                                flagId: progress.flagId,
                                status: progress.status,
                                unflaggedReason: progress.unflaggedReason,
                                flagProgressId: progress.flagProgressId,
                            });
                        });
                    });
                });

                return fields;
            });

            // sort youth based on filter
            const sorted = youths.sort(customYouthSorting);

            setYouthList(sorted);
        }

        // Plans are queried by custom assignment handle
        // Example: assignmentHandle: [
        //     "onboarding_youth"
        //     "onboarding_caregiver"
        //     "22_washington_youth" // 22 = group_id of the careCoordinator
        //     "22_washington_caregiver"]
        // Each surevey within an assignment is one week of questions
        // Each assignment is deployed by itself for use by caregivers or youth
        // The name that appears on the list of plans in the "Add Plan" Modal is based off of the deploymentHandle
        async function getAllPlansList() {
            let assignmentHandles = [];
            assignmentHandles.push(..._get(ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES, 'onboarding', []));

            // custom state plans
            const groupId = _get(window, 'cccisd.fortress.user.acting.group_id', '-1').toString();
            assignmentHandles.push(..._get(ORG_CUSTOM_PLAN_ASSIGNMENT_HANDLES, groupId, []));

            const response = await client.query({
                query: getAllPlansListQuery,
                variables: { assignment_handles: assignmentHandles },
                fetchPolicy: 'network-only',
            });

            let plans = {};

            _get(response, 'data.flows.assignmentList', []).forEach(assignment => {
                let assignmentLabel = assignment.label;

                const { assignmentHandle } = assignment;

                // list of main parent deployments for each plan (weekly)
                const deploymentHandles = assignment.deploymentList.filter(
                    deployment =>
                        deployment.timepoint === null &&
                        deployment.parentDeploymentId === null &&
                        deployment.deploymentHandle
                );

                let planName = '';
                if (deploymentHandles.length > 0) {
                    planName = deploymentHandles[0].deploymentHandle.split('_')[0].trim().toUpperCase();
                }

                if (!_get(plans, planName, false)) {
                    if (!planName) {
                        return;
                    }

                    plans[planName] = {
                        planName,
                        youthDeploymentHandles: [],
                        caregiverDeploymentHandles: [],
                        assignments: [],
                        isOrgPlan: assignmentHandle.startsWith(groupId) ? true : false,
                    };
                }

                if (deploymentHandles.length > 0) {
                    if (deploymentHandles[0].deploymentHandle.includes('youth')) {
                        plans[planName].youthDeploymentHandles = deploymentHandles.map(val => val.deploymentHandle);
                    } else if (deploymentHandles[0].deploymentHandle.includes('caregiver')) {
                        plans[planName].caregiverDeploymentHandles = deploymentHandles.map(val => val.deploymentHandle);
                    }
                }

                let assignmentInfo = { assignmentLabel };

                let surveys = _get(assignment, 'surveyList', []).map(survey => {
                    let info = {
                        surveyLabel: survey.label,
                        surveyQuestions: [],
                    };

                    _get(survey, 'flowList', []).forEach(flow => {
                        _get(flow, 'entityList', []).forEach(entity => {
                            if (_get(entity, 'content.question', false)) {
                                info.surveyQuestions.push(entity.content.question);
                            }
                        });
                    });

                    return info;
                });

                assignmentInfo.surveys = surveys;
                plans[planName].assignments.push(assignmentInfo);
            });

            setPlansList(plans);
        }

        getAllYouthList();
        getAllPlansList();
    }, [alertsEditedSignal]);

    // query list of all administered plans
    useEffect(() => {
        async function getAdministeredPlans() {
            const response = await client.query({
                query: getAdministeredPlansQuery,
                fetchPolicy: 'network-only',
            });

            let administeredYouthPlans = {};
            _get(response, 'data.roles.youthList', []).forEach(youth => {
                let pawnId = youth.pawn.pawnId;
                let plans = youth.selectedAssignmentPlanList;

                plans.forEach(plan => {
                    const originalStartDate = JSON.parse(plan.settings).startDate;
                    const isStopped = _get(JSON.parse(plan.settings), 'stopPlan', false);
                    const hasNotOpenedYet = !isDateInPast(originalStartDate);

                    const isClosed =
                        plan.sessionList.filter(
                            session => session.deployment.isOpen || !isDateInPast(session.opensDate)
                        ).length === 0;

                    plan.isStopped = isStopped;
                    plan.hasNotOpenedYet = hasNotOpenedYet;
                    plan.isClosed = isClosed;
                    plan.wrapIds = [
                        { pawnId: youth.pawn.pawnId, wrapId: youth.fields.wrapId },
                        ...youth.parentRoles.caregiverList.map(caregiver => {
                            return {
                                pawnId: caregiver.pawn.pawnId,
                                wrapId: caregiver.fields.wrapId,
                            };
                        }),
                    ];
                });

                administeredYouthPlans[pawnId] = plans;
            });

            setYouthPlans(administeredYouthPlans);
        }

        getAdministeredPlans();
    }, []);

    const synchronizeValues = values => {
        if (startDateValue) {
            values.startDate = getFormattedDateForDB(startDateValue);
        }

        values.youthSelected = selectedYouth;
        values.primaryCaregiverPlan = _get(selectedPlans, 'primaryCaregiverPlan[0]', '');
        values.primaryYouthPlan = _get(selectedPlans, 'primaryYouthPlan[0]', '');
        values.orgCaregiverPlan = _get(selectedPlans, 'orgCaregiverPlan[0]', '');
        values.orgYouthPlan = _get(selectedPlans, 'orgYouthPlan[0]', '');

        return values;
    };

    const onHandleSubmit = async values => {
        values = synchronizeValues(values);

        await onSubmit(values);
    };

    const validate = values => {
        const errors = {};

        const validatingValues = synchronizeValues(values);

        if (!validatingValues.primaryCaregiverPlan && !validatingValues.orgCaregiverPlan) {
            errors.selectedPlans = 'A caregiver plan is required to administer.';
        }
        if (validatingValues.primaryYouthPlan && validatingValues.orgYouthPlan) {
            errors.selectedPlans = 'A youth plan is required to administer.';
        }
        if (!validatingValues.startDate) {
            errors.startDate = 'A start date is required to administer.';
        }
        if (!validatingValues.youthSelected || validatingValues.youthSelected.length === 0) {
            errors.youthSelected = 'One or more youth are required to administer.';
        }

        return errors;
    };

    const initialValues = { autorenew: true };

    const onSubmit = async values => {
        const response = await axios.post(Boilerplate.route('app.plan.store'), {
            startDate: values.startDate,
            youthSelected: values.youthSelected,
            orgCaregiverPlan: values.orgCaregiverPlan,
            orgYouthPlan: values.orgYouthPlan,
            primaryCaregiverPlan: values.primaryCaregiverPlan,
            primaryYouthPlan: values.primaryYouthPlan,
        });

        if (response.data.errors) {
            notification({ message: 'Error submitting plan.', type: 'danger' });
            return Promise.reject(response.data.errors);
        }

        notification({ message: 'Plan successfully scheduled.', duration: 5000 });
        await assignPlanModalRef.current.close();

        window.location.reload();
    };

    return (
        <div>
            <div style={{ padding: '30px' }}>
                {youthList.length > 0 && (
                    <div>
                        <div className={style.new_plan_button_box}>
                            <div style={{ marginRight: '15px' }}>
                                <Modal
                                    trigger={
                                        <button type="button" className="btn btn-dark">
                                            Assign New Plan
                                        </button>
                                    }
                                    ref={assignPlanModalRef}
                                    title="Assign New Plan"
                                    size="80vw"
                                    render={({ closeModal }) => {
                                        return (
                                            <div style={{ margin: '15px' }}>
                                                <Formik
                                                    onSubmit={e => onHandleSubmit(e)}
                                                    validate={e => validate(e)}
                                                    initialValues={initialValues}
                                                >
                                                    {({ handleSubmit, values, setFieldValue }) => (
                                                        <Form>
                                                            <div className={style.assign_plan_section}>
                                                                <div className={style.number}>1</div>
                                                                <div>
                                                                    <Field name="startDate">
                                                                        {({ field, form }) => {
                                                                            return (
                                                                                <CccisdFieldWrapper
                                                                                    label={
                                                                                        <div>
                                                                                            <RequiredFieldLabel
                                                                                                isRequired
                                                                                            >
                                                                                                Start Date:
                                                                                            </RequiredFieldLabel>
                                                                                        </div>
                                                                                    }
                                                                                    form={form}
                                                                                    field={field}
                                                                                >
                                                                                    <div>
                                                                                        <DatePicker
                                                                                            id="startDateValue"
                                                                                            selected={
                                                                                                startDateValue
                                                                                                    ? startDateValue
                                                                                                    : null
                                                                                            }
                                                                                            filterDate={isMonday}
                                                                                            onChange={val => {
                                                                                                setStartDateValue(val);
                                                                                            }}
                                                                                            placeholderText="-- Select a Monday to start --"
                                                                                            dateFormat="MM/dd/yyyy"
                                                                                        />
                                                                                    </div>
                                                                                    <div
                                                                                        style={{
                                                                                            marginTop: '20px',
                                                                                        }}
                                                                                        className={style.tooltip_text}
                                                                                    >
                                                                                        Start Date determines the first
                                                                                        date that messages will start.
                                                                                        Messages are sent on Mondays
                                                                                        ~10AM EST for caregivers and
                                                                                        ~4PM EST for youth.
                                                                                    </div>
                                                                                </CccisdFieldWrapper>
                                                                            );
                                                                        }}
                                                                    </Field>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className={style.assign_plan_section}
                                                                style={{ flexWrap: 'wrap' }}
                                                            >
                                                                <div className={style.number}>2</div>

                                                                <div className={style.select_youth_table_wrapper}>
                                                                    <Field name="youthSelected">
                                                                        {({ field, form }) => {
                                                                            return (
                                                                                <CccisdFieldWrapper
                                                                                    label={
                                                                                        <div
                                                                                            style={{
                                                                                                marginBottom: '8px',
                                                                                            }}
                                                                                        >
                                                                                            <RequiredFieldLabel
                                                                                                isRequired
                                                                                            >
                                                                                                Select Youth:
                                                                                            </RequiredFieldLabel>
                                                                                        </div>
                                                                                    }
                                                                                    form={form}
                                                                                    field={field}
                                                                                >
                                                                                    <>
                                                                                        <YouthSelectionTable
                                                                                            youthList={youthList}
                                                                                            youthPlans={youthPlans}
                                                                                            setSelectedYouth={
                                                                                                setSelectedYouth
                                                                                            }
                                                                                            youthSelectionTableSelectedRows={
                                                                                                youthSelectionTableSelectedRows
                                                                                            }
                                                                                        />
                                                                                    </>
                                                                                </CccisdFieldWrapper>
                                                                            );
                                                                        }}
                                                                    </Field>
                                                                </div>
                                                            </div>
                                                            <div className={style.assign_plan_section}>
                                                                <div
                                                                    className={style.number}
                                                                    style={{ marginBottom: '25px' }}
                                                                >
                                                                    3
                                                                </div>

                                                                <div className={style.flex_column}>
                                                                    <Field
                                                                        name="autorenew"
                                                                        component={CccisdToggle}
                                                                        label={<b>Auto Renew Plan</b>}
                                                                    />
                                                                    {values.autorenew && (
                                                                        <div className={style.tooltip_text}>
                                                                            The SMS survey messages will re-start once
                                                                            the last survey message of the plan is sent.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div
                                                                className={style.assign_plan_section}
                                                                style={{
                                                                    borderBottom: '0px',
                                                                }}
                                                            >
                                                                <div className={style.number}>4</div>

                                                                <div style={{ width: '90%' }}>
                                                                    <Field name="selectedPlans">
                                                                        {({ field, form }) => {
                                                                            return (
                                                                                <CccisdFieldWrapper
                                                                                    label={
                                                                                        <div>
                                                                                            <RequiredFieldLabel
                                                                                                isRequired
                                                                                            >
                                                                                                Select Plan(s) to
                                                                                                administer:
                                                                                            </RequiredFieldLabel>
                                                                                        </div>
                                                                                    }
                                                                                    form={form}
                                                                                    field={field}
                                                                                />
                                                                            );
                                                                        }}
                                                                    </Field>
                                                                    {Object.keys(plansList).map((planPhase, i) => (
                                                                        <div key={i}>
                                                                            <PlanCard
                                                                                plan={plansList[planPhase]}
                                                                                selectedPlans={selectedPlans}
                                                                                setSelectedPlans={setSelectedPlans}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            <div className={style.submit_button}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-secondary"
                                                                    onClick={() => handleSubmit()}
                                                                >
                                                                    Submit
                                                                </button>
                                                            </div>
                                                        </Form>
                                                    )}
                                                </Formik>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                            <div>Administer plan(s) consisting of weekly surveys to youth and their caregivers</div>
                        </div>
                        <br />

                        {/* filter area  & youth cards container */}
                        <div className={style.main_container}>
                            {/* filters container  */}
                            <div className={style.filters_container}>
                                <div className={style.filters_flex_row}>
                                    <div>
                                        <label className="form-label">Search: </label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            placeholder="Name / WrapID"
                                            value={filters.search}
                                            onChange={e => {
                                                const newValue = e.target.value;
                                                setFilters(prev => ({
                                                    ...prev,
                                                    search: newValue,
                                                }));
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Show: </label>
                                        <select
                                            className="form-select"
                                            ref={showFilterRef}
                                            onChange={e => {
                                                const newValue = e.target.value;
                                                setFilters(prev => ({
                                                    ...prev,
                                                    show: newValue,
                                                }));
                                            }}
                                        >
                                            <option value="all">All Youth</option>
                                            <option value="active_plan">With Active Plan</option>
                                            <option value="no_plan">Without Active Plan</option>
                                            {/* <option value={'alerts'}>With Alerts</option> */}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label">Sort By: </label>
                                        <select
                                            className="form-select"
                                            ref={sortByFilterRef}
                                            onChange={e => {
                                                const newValue = e.target.value;
                                                setFilters(prev => ({
                                                    ...prev,
                                                    sortBy: newValue,
                                                }));
                                            }}
                                        >
                                            <option value="alerts">Alerts</option>
                                            <option value="first_name">First Name</option>
                                            <option value="last_name">Last Name</option>
                                            <option value="wrapid">WrapID</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label">Plan Name: </label>
                                        <select
                                            className="form-select"
                                            ref={planNameFilterRef}
                                            onChange={e => {
                                                const newValue = e.target.value;
                                                setFilters(prev => ({
                                                    ...prev,
                                                    planName: newValue,
                                                }));
                                            }}
                                        >
                                            <option value="" />
                                            {Object.keys(plansList)
                                                .filter(val => val)
                                                .map((planName, i) => {
                                                    return (
                                                        <option key={i} value={planName.toLowerCase()}>
                                                            {planName}
                                                        </option>
                                                    );
                                                })}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* youth cards start */}
                            {youthList.sort(customYouthSorting).map((youth, i) => {
                                const activePlans = _get(youthPlans, [youth.pawnId], []).filter(plan => {
                                    const status = getPlanStatus(plan);
                                    return status === 'Active' || status === 'Scheduled';
                                });

                                const hasActivePlan = activePlans.length > 0;

                                const activePlanName = hasActivePlan
                                    ? JSON.parse(activePlans[0].settings).planName.toLowerCase()
                                    : '';

                                const card = (
                                    <div key={i}>
                                        {
                                            <YouthCard
                                                youth={youth}
                                                key={i}
                                                i={i}
                                                setAlertsEditedSignal={setAlertsEditedSignal}
                                                youthPlans={youthPlans}
                                                assignPlanModalRef={assignPlanModalRef}
                                                setYouthSelectionTableSelectedRows={setYouthSelectionTableSelectedRows}
                                            />
                                        }
                                    </div>
                                );

                                const blank = <div key={i} />;

                                if (filters) {
                                    const showFilterValue = filters.show;
                                    const searchValue = (filters.search || '').toLowerCase();
                                    const planNameValue = (filters.planName || '').toLowerCase();

                                    const youthName = `${youth.firstName || ''} ${youth.lastName || ''}`.toLowerCase();
                                    const youthWrapId = (youth.wrapId || '').toLowerCase();

                                    const caregiverNames = youth.caregiverList
                                        .map(caregiver =>
                                            `${caregiver.fields.firstName || ''} ${
                                                caregiver.fields.lastName || ''
                                            }`.toLowerCase()
                                        )
                                        .join(' ');

                                    const caregiverWrapIds = youth.caregiverList
                                        .map(caregiver => {
                                            if (caregiver.fields.wrapId) {
                                                return caregiver.fields.wrapId.toLowerCase();
                                            }
                                            return '';
                                        })
                                        .join(' ');

                                    const searchableFields = [youthName, youthWrapId, caregiverNames, caregiverWrapIds];

                                    const matchesPlanName = !planNameValue || activePlanName === planNameValue;

                                    const matchesSearch =
                                        !searchValue || searchableFields.some(field => field?.includes(searchValue));

                                    if (!matchesPlanName || !matchesSearch) return blank;

                                    switch (showFilterValue) {
                                        case 'active_plan':
                                            return hasActivePlan ? card : blank;
                                        case 'no_plan':
                                            return !hasActivePlan ? card : blank;
                                        default:
                                            return card;
                                    }
                                }

                                return blank;
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Administer;
